import {
  calculateDailyTotals,
  formatLogDate,
  formatLogTime,
  getLogListedServing,
  getLogPortions,
  groupLogsByDate,
} from "./v2FoodLogUtils";
import { getSafeQuantityUnit, toFoodNumber } from "./v2FoodUtils";

const VALID_REPORT_DAYS = Object.freeze([1, 14, 30]);

export function getReportDateRange(days = 14, referenceDate = new Date()) {
  const safeDays = VALID_REPORT_DAYS.includes(Number(days)) ? Number(days) : 14;
  const end = normalizeDate(referenceDate);
  const start = new Date(end);
  start.setDate(start.getDate() - safeDays + 1);

  const dates = [];
  for (let index = 0; index < safeDays; index += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    dates.push(formatLogDate(date));
  }

  return {
    days: safeDays,
    startDate: formatLogDate(start),
    endDate: formatLogDate(end),
    dates,
  };
}

export function buildV2HistoryReport({
  logs = [],
  days = 14,
  selectedDate,
  profile = {},
  generatedAt = new Date(),
} = {}) {
  const safeDays = VALID_REPORT_DAYS.includes(Number(days)) ? Number(days) : 14;
  const rangeReferenceDate = safeDays === 1 && selectedDate ? selectedDate : generatedAt;
  const range = getReportDateRange(safeDays, rangeReferenceDate);
  const groupedLogs = groupLogsByDate(logs);
  const dailyRows = range.dates.map((dateKey) => {
    const dateLogs = groupedLogs[dateKey] || [];
    return {
      date: dateKey,
      itemCount: dateLogs.length,
      totals: calculateDailyTotals(dateLogs),
      items: dateLogs.map(normalizeReportItem),
    };
  });

  return {
    appName: "Dialysis Diet Pro",
    title: range.days === 1 ? "Daily Food History Report" : "Food History Report",
    patientName: profile.full_name || profile.fullName || "Patient",
    weightKg: profile.weight_kg || profile.weight || "",
    generatedAt: generatedAt instanceof Date ? generatedAt.toISOString() : new Date(generatedAt).toISOString(),
    range,
    dailyRows,
  };
}

export function getReportFilename(days = 14) {
  const safeDays = VALID_REPORT_DAYS.includes(Number(days)) ? Number(days) : 14;
  return safeDays === 1 ? "dialysis-diet-pro-report-1-day.pdf" : `dialysis-diet-pro-report-${safeDays}-days.pdf`;
}

export function buildV2ReportDetailRows(report = {}) {
  const dailyRows = Array.isArray(report.dailyRows) ? report.dailyRows : [];

  return dailyRows.flatMap((day) => {
    const items = Array.isArray(day.items) ? day.items : [];
    if (items.length === 0) {
      return report.range?.days === 1
        ? [[day.date, "", "No food logged for this date.", "", "", "", "", "", ""]]
        : [];
    }

    return items.map((item) => [
      day.date,
      item.time,
      item.foodName,
      item.portions > 0 ? formatReportAmount(item.portions) : "-",
      formatReportServing(item),
      `${formatReportAmount(item.potassium_mg)} mg`,
      `${formatReportAmount(item.phosphorus_mg)} mg`,
      `${formatReportAmount(item.sodium_mg)} mg`,
      `${formatReportAmount(item.protein_g)} g`,
    ]);
  });
}

export function formatReportServing(item = {}) {
  if (item.servingQuantity > 0 && item.servingUnit) {
    return `${formatReportAmount(item.servingQuantity)} ${item.servingUnit}`;
  }

  return `${formatReportAmount(item.quantity)} ${item.quantityUnit}`;
}

function normalizeReportItem(log = {}) {
  const listedServing = getLogListedServing(log);

  return {
    time: formatLogTime(log.created_at),
    foodName: log.food_name_snapshot || "Food item",
    quantity: toFoodNumber(log.quantity),
    quantityUnit: getSafeQuantityUnit(log.quantity_unit, "serving"),
    portions: getLogPortions(log),
    servingQuantity: listedServing?.quantity || 0,
    servingUnit: listedServing?.unit || "",
    potassium_mg: toFoodNumber(log.potassium_mg_snapshot),
    phosphorus_mg: toFoodNumber(log.phosphorus_mg_snapshot),
    sodium_mg: toFoodNumber(log.sodium_mg_snapshot),
    protein_g: toFoodNumber(log.protein_g_snapshot),
  };
}

function normalizeDate(value) {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  date.setHours(0, 0, 0, 0);
  return date;
}

function formatReportAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}
